import { ServiceLayout } from "@/components/ServiceLayout"
import { aiopsFAQs } from "@/data/faqData"

const AIOps = () => {
  return (
    <ServiceLayout
      title="AIOps & Managed Services"
      description="Utilizing AIOps platforms to detect anomalies and resolve infrastructure incidents before they impact business operations."
      heroImage="https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=2600"
      faqs={aiopsFAQs}
      faqTitle="AIOps FAQs"
      faqSubtitle="Common questions about AI-powered IT operations and automation"
      features={[
        {
          title: "Automated Remediation",
          description:
            "Scripts that automatically restart services, clear caches, or reroute traffic when specific failure patterns are detected.",
        },
        {
          title: "Anomaly Detection",
          description:
            "Machine learning models that establish baselines for normal performance and alert only on true deviations, reducing alert fatigue.",
        },
        {
          title: "Root Cause Analysis (RCA)",
          description:
            "AI that correlates logs across distributed systems to instantly pinpoint the source of an outage.",
        },
      ]}
      approach={{
        title: "Autonomous Reliability",
        description:
          "AI systems are not 'fire and forget'. They are gardens that need tending.",
        image:
          "https://images.unsplash.com/photo-1618401471353-b98afee0b2eb?auto=format&fit=crop&q=80&w=2600",
        content: [
          "AI systems are not 'fire and forget'. They are probabilistic gardens that need tending. We implement 'Continuous Observability 2.0' where we monitor not just system metrics (CPU/RAM), but model metrics (Drift, Confidence, Bias).",
          "We derive the solution by establishing 'Baselines of Normality' over a 2-week period. Once established, our anomaly detection models flag deviations in real-time. If latency spikes or accuracy drops, the system triggers automated remediation - restarting pods, rolling back weights, or clearing caches - without human intervention.",
          "This creates a self-healing infrastructure that guarantees 99.99% uptime for your mission-critical AI workloads.",
        ],
      }}
      benefits={[
        "90% Faster MTTR",
        "Reduced Alert Noise",
        "Proactive Incident Prevention",
        "24/7/365 Monitoring",
        "Capacity Planning Insights",
        "SLO/SLA Management",
      ]}
      process={{
        steps: [
          {
            title: "Baseline Establishment",
            description:
              "Collecting telemetry data (logs, metrics, traces) for 2-4 weeks to understand normal system behavior and seasonality.",
          },
          {
            title: "Correlation Rules",
            description:
              "Configuring the AIOps platform to group related alerts from different systems into single verifiable incidents.",
          },
          {
            title: "Automated Remediation",
            description:
              "Building runbooks for common issues (disk full, memory leak, service hang) that trigger automatically upon detection.",
          },
          {
            title: "Feedback Loop",
            description:
              "Continuously training the models based on incident resolution data to improve accuracy and reduce false positives.",
          },
        ],
      }}
      techStack={[
        {
          category: "Observability",
          items: ["Datadog", "New Relic", "Prometheus", "Grafana"],
        },
        {
          category: "Log Management",
          items: ["Elasticsearch (ELK)", "Splunk", "Fluentd"],
        },
        {
          category: "Incident Response",
          items: ["PagerDuty", "Opsgenie", "ServiceNow ITOM"],
        },
      ]}
    />
  )
}

export default AIOps
